import { DateRange } from './contracts'

export interface PublicAdministrationApiFilterItem {
  field: string
  condition: string
  comparison: string
}

export const PUBLIC_ADMINISTRATION_FILTER_STATE_KEYS = [
  'pa_administration_type',
  'pa_state',
  'pa_population_size',
  'pa_has_innovation_department',
  'pa_has_ct_budget',
  'pa_impact_area',
  'pa_procedure',
  'pa_program_goal'
] as const

export type PublicAdministrationFilterStateKey = (typeof PUBLIC_ADMINISTRATION_FILTER_STATE_KEYS)[number]

export interface PublicAdministrationFilterCounts {
  pa_administration_type: number
  pa_state: number
  pa_population_size: number
  pa_has_innovation_department: number
  pa_has_ct_budget: number
  pa_impact_area: number
  pa_procedure: number
  pa_program_goal: number
}

const publicAdministrationFilterKeyToApiField: Record<PublicAdministrationFilterStateKey, string> = {
  pa_administration_type: 'public_administration.administration_type',
  pa_state: 'public_administration.state',
  pa_population_size: 'public_administration.population_size',
  pa_has_innovation_department: 'leadership_governance.has_innovation_department',
  pa_has_ct_budget: 'innovation_profile.has_ct_budget',
  pa_impact_area: 'public_administration.impact_area',
  pa_procedure: 'public_administration.procedure',
  pa_program_goal: 'public_administration.program_goal'
}

const publicAdministrationPipeInApiFields = new Set<string>([
  'public_administration.administration_type',
  'public_administration.state',
  'leadership_governance.has_innovation_department',
  'innovation_profile.has_ct_budget'
])


const publicAdministrationExistsOneValuePerClauseApiFields = new Set<string>([
  'public_administration.procedure',
  'public_administration.impact_area',
  'public_administration.program_goal'
])

export const emptyPublicAdministrationSelectedFilters = (): Record<
  PublicAdministrationFilterStateKey,
  string[]
> => ({
  pa_administration_type: [],
  pa_state: [],
  pa_population_size: [],
  pa_has_innovation_department: [],
  pa_has_ct_budget: [],
  pa_impact_area: [],
  pa_procedure: [],
  pa_program_goal: []
})

export const emptyPublicAdministrationFilterCounts = (): PublicAdministrationFilterCounts => ({
  pa_administration_type: 0,
  pa_state: 0,
  pa_population_size: 0,
  pa_has_innovation_department: 0,
  pa_has_ct_budget: 0,
  pa_impact_area: 0,
  pa_procedure: 0,
  pa_program_goal: 0
})

export const computePublicAdministrationFilterCounts = (
  selected: Record<string, string[]>
): PublicAdministrationFilterCounts => ({
  pa_administration_type: selected.pa_administration_type?.length ?? 0,
  pa_state: selected.pa_state?.length ?? 0,
  pa_population_size: selected.pa_population_size?.length ?? 0,
  pa_has_innovation_department: selected.pa_has_innovation_department?.length ?? 0,
  pa_has_ct_budget: selected.pa_has_ct_budget?.length ?? 0,
  pa_impact_area: selected.pa_impact_area?.length ?? 0,
  pa_procedure: selected.pa_procedure?.length ?? 0,
  pa_program_goal: selected.pa_program_goal?.length ?? 0
})

export const buildPublicAdministrationFilterBy = (
  selectedFilters: Record<string, string[]>,
  companyNameSearchTrimmed: string,
  registerDateRange: DateRange
): PublicAdministrationApiFilterItem[] => {
  const filterBy: PublicAdministrationApiFilterItem[] = []

  Object.entries(selectedFilters).forEach(([field, values]) => {
    if (values.length === 0) return
    const mappedField =
      publicAdministrationFilterKeyToApiField[field as PublicAdministrationFilterStateKey] ?? field

    if (publicAdministrationExistsOneValuePerClauseApiFields.has(mappedField)) {
      values.forEach(value => {
        filterBy.push({
          field: mappedField,
          condition: value,
          comparison: 'eq'
        })
      })
      return
    }

    if (publicAdministrationPipeInApiFields.has(mappedField)) {
      if (values.length === 1) {
        filterBy.push({
          field: mappedField,
          condition: values[0],
          comparison: 'eq'
        })
      } else {
        filterBy.push({
          field: mappedField,
          condition: values.join('|'),
          comparison: 'in'
        })
      }
      return
    }

    values.forEach(value => {
      filterBy.push({
        field: mappedField,
        condition: value,
        comparison: 'eq'
      })
    })
  })

  if (companyNameSearchTrimmed !== '') {
    filterBy.push({
      field: 'company.name',
      condition: companyNameSearchTrimmed,
      comparison: 'lk'
    })
  }

  const paDateField = 'public_administration.created_at'
  if (registerDateRange.start && registerDateRange.end) {
    filterBy.push({
      field: paDateField,
      condition: `${registerDateRange.start.toISOString().slice(0, 10)} ${registerDateRange.end.toISOString().slice(0, 10)}`,
      comparison: 'bt'
    })
  } else if (registerDateRange.start) {
    filterBy.push({
      field: paDateField,
      condition: registerDateRange.start.toISOString().slice(0, 10),
      comparison: 'eq'
    })
  }

  return filterBy
}
